import { Graphics, Sprite, Texture } from 'pixi.js';
import type { DechaineEffect } from '../../data/full/avatar';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import { AIR, FIRE, WATER } from '../../sim/full/attacks/cycle';
import {
  UNLEASHED,
  isChained,
  unleashedReach,
  unleashedTicks,
} from '../../sim/full/skills/dechaine';
import type { PropState, SimState } from '../../sim/state';
import { statValue } from '../../sim/stats';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { add, byId, hide, setTint } from '../util';
import { ViewPool } from '../views';
import { type FlagHold, type Point, flagTip, holdOf } from './avatar-flags';
import {
  FACES,
  GIANT_PIECES,
  KOI_TAIL_ROOT,
  RAY,
  THIRD_EYE,
  WING_ROOT,
} from './avatar-giants-paint';
import { ELEMENT_COLORS } from './avatar-paint';
import { Silk, type SilkColors } from './avatar-silk';

type GiantShapes = Readonly<Record<keyof typeof GIANT_PIECES, Shape>>;

const DENSITY = 2;
const OUT = 0x040612;
const RAY_TINTS = [ELEMENT_COLORS[0], ELEMENT_COLORS[1], 0xbfe3ff, ELEMENT_COLORS[3]] as const;
// Beats from the cast: the giant rises out of the flags, then strikes.
const GIANT = {
  size: 1.6,
  rise: 0.3,
  strike: 0.5,
  appear: 0.12,
  eyes: { from: 0.1, over: 0.15 },
  third: { from: 0.25, over: 0.12 },
  fade: 0.4,
} as const;
const DARK = {
  color: 0x03040e,
  alpha: 0.62,
  calmAlpha: 0.3,
  rise: 0.25,
  hold: 1.2,
  fall: 0.8,
} as const;
const RAYS = {
  count: 12,
  long: 900,
  short: 560,
  narrow: 0.6,
  alpha: 0.6,
  calmAlpha: 0.33,
  calmSlower: 1.3,
  shine: 0.55,
} as const;
const SHOCK = { beats: 0.9, scale: 1.8 } as const;
const FLASH = { radius: 64, alpha: 0.25, decay: 6 } as const;
const CAST = { beats: 0.6, from: 12, to: 58, glow: 40 } as const;
const STREAMS = { beats: 0.55, alpha: 0.8, width: 6, core: 2 } as const;
const PHOENIX = { lift: 80, toward: 0.4, flap: 0.12, flapPerBeat: 1.5 } as const;
const KOI = { lift: 90, toward: 0.3, swish: 0.12, pool: { rx: 52, ry: 24, ripples: 3 } } as const;
const FIST = { ahead: 60, lift: 110 } as const;
const DRAGON = {
  points: 44,
  tail: 0.9,
  turn: 0.9,
  from: 1.4,
  to: 0.85,
  squash: 0.76,
  width: 19,
  grow: 0.4,
  leave: 1.6,
  gone: 0.5,
  mane: { from: 2, to: 30, width: 0.45, away: 2.4, wave: 3 },
} as const;
const AIR_SILK: SilkColors = {
  band: 0x9cc4ee,
  bandLine: 0xffffff,
  light: 0xffffff,
  lightLine: 0xffffff,
};
const DEBRIS = {
  count: 7,
  from: 26,
  to: 74,
  lift: 16,
  beats: 0.7,
  size: 4.5,
  color: 0xc99050,
} as const;

interface GiantView {
  readonly glow: Sprite;
  readonly rays: readonly Sprite[];
  readonly flash: Sprite;
  readonly castGlow: Sprite;
  readonly thirdGlow: Sprite;
  readonly limbs: readonly [Sprite, Sprite];
  readonly body: Sprite;
  readonly eyes: Sprite;
  readonly third: Sprite;
}

interface Pose {
  x: number;
  y: number;
  scale: number;
  alpha: number;
  turn: number;
  limb: number;
  strikeX: number;
  strikeY: number;
}

export function createAvatarGiants(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const unleashes = new Map<string, DechaineEffect>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'dechaine') {
      unleashes.set(classId, effect);
    }
  }
  const sheet = paintedLater((): Sheet<keyof typeof GIANT_PIECES> =>
    paintSheet(GIANT_PIECES, DENSITY),
  );
  const dark = layers.dim.addChild(new Sprite(Texture.WHITE));
  dark.visible = false;
  const ground = layers.zones.addChild(new Graphics());
  const light = layers.fx.addChild(new Graphics());
  const ribbons = layers.bubbles.addChild(new Graphics());
  const body = new Silk(DRAGON.points + 1);
  const mane = new Silk(DRAGON.mane.to - DRAGON.mane.from);
  const bodyX = new Float64Array(DRAGON.points + 1);
  const bodyY = new Float64Array(DRAGON.points + 1);
  const hold: FlagHold = { x: 0, y: 0, facing: 0, size: 1, beats: 0, calm: false };
  const tip: Point = { x: 0, y: 0 };
  const head: Point = { x: 0, y: 0 };
  const apex: Point = { x: 0, y: 0 };
  const pose: Pose = { x: 0, y: 0, scale: 1, alpha: 1, turn: 0, limb: 0, strikeX: 0, strikeY: 0 };
  let darkness = 0;
  let views: ViewPool<GiantView> | undefined;
  let eyeShapes: readonly Shape[] = [];
  let thirdShapes: readonly Shape[] = [];
  let bodyShapes: readonly Shape[] = [];

  function viewPool(shapes: GiantShapes): ViewPool<GiantView> {
    if (views !== undefined) {
      return views;
    }
    eyeShapes = [shapes.eyes0, shapes.eyes1, shapes.eyes2, shapes.eyes3];
    thirdShapes = [shapes.third0, shapes.third1, shapes.third2, shapes.third3];
    bodyShapes = [shapes.phoenix, shapes.koi, shapes.dragonHead, shapes.fist];
    views = new ViewPool<GiantView>(
      () => ({
        glow: add(layers.fx, textures.halo),
        rays: Array.from({ length: RAYS.count }, () => add(layers.fx, shapes.ray, 0)),
        flash: add(layers.fx, textures.halo),
        castGlow: add(layers.fx, textures.halo),
        thirdGlow: add(layers.fx, textures.halo),
        limbs: [
          add(layers.bubbles, shapes.phoenixWing, 0),
          add(layers.bubbles, shapes.phoenixWing, 0),
        ],
        body: add(layers.bubbles, shapes.phoenix),
        eyes: add(layers.bubbles, shapes.eyes0),
        third: add(layers.bubbles, shapes.third0),
      }),
      (view) => {
        hide(view.glow, ...view.rays, view.flash, view.castGlow, view.thirdGlow);
        hide(...view.limbs, view.body, view.eyes, view.third);
      },
    );
    return views;
  }

  function rise(beats: number): number {
    return smoothstep(clamp01(beats / GIANT.rise));
  }

  function dive(beats: number): number {
    return clamp01((beats - GIANT.rise) / (GIANT.strike - GIANT.rise)) ** 2;
  }

  function arc(beats: number, start: Point, apex: Point, from: number, to: number): void {
    const apexX = apex.x;
    const apexY = apex.y;
    if (beats < GIANT.rise) {
      const k = rise(beats);
      pose.x = lerp(start.x, apexX, k);
      pose.y = lerp(start.y, apexY, k);
      pose.scale = lerp(0.25, from, k);
    } else {
      const k = dive(beats);
      pose.x = lerp(apexX, pose.strikeX, k);
      pose.y = lerp(apexY, pose.strikeY, k);
      pose.scale = lerp(from, to, k);
    }
  }

  function afterStrike(beats: number, stay: number): number {
    return 1 - smoothstep(clamp01((beats - GIANT.strike - stay) / GIANT.fade));
  }

  function posePhoenix(loose: PropState, beats: number, calm: boolean): void {
    pose.strikeX = loose.x;
    pose.strikeY = loose.y;
    apex.x = lerp(hold.x, loose.x, PHOENIX.toward);
    apex.y = lerp(hold.y, loose.y, PHOENIX.toward) - PHOENIX.lift;
    arc(beats, hold, apex, 1.05, 0.8);
    pose.turn = 0;
    if (beats >= GIANT.strike) {
      pose.scale = 0.8 + 0.5 * (beats - GIANT.strike);
    }
    pose.alpha = afterStrike(beats, 0);
    const flap = calm ? 0 : Math.sin(TAU * PHOENIX.flapPerBeat * beats);
    pose.limb = beats < GIANT.rise ? flap : beats < GIANT.strike ? -1 : 1;
  }

  function poseKoi(
    loose: PropState,
    effect: DechaineEffect,
    size: number,
    beats: number,
    frame: Frame,
  ) {
    const strikeTicks = GIANT.strike * frame.tempo.ticksPerBeat;
    const reach = unleashedReach(effect, WATER, strikeTicks, size, frame.tempo);
    pose.strikeX = loose.x + loose.vx * reach;
    pose.strikeY = loose.y + loose.vy * reach;
    apex.x = lerp(loose.x, pose.strikeX, KOI.toward);
    apex.y = lerp(loose.y, pose.strikeY, KOI.toward) - KOI.lift;
    arc(beats, loose, apex, 1, 0.6);
    pose.turn = beats < GIANT.strike ? 0.25 * Math.sin(TAU * 0.4 * beats) : 0;
    pose.alpha = afterStrike(beats, 0);
    pose.limb = frame.calm ? 0 : KOI.swish * Math.sin(TAU * 0.8 * beats);
  }

  function poseFist(loose: PropState, beats: number): void {
    pose.strikeX = loose.x + loose.vx * FIST.ahead;
    pose.strikeY = loose.y + loose.vy * FIST.ahead;
    apex.x = pose.strikeX;
    apex.y = pose.strikeY - FIST.lift;
    arc(beats, hold, apex, 1.05, 0.7);
    pose.turn = 0;
    pose.alpha = afterStrike(beats, 0.6);
    pose.limb = 0;
  }

  function dragonHead(beats: number, cx: number, cy: number, radius: number): Point {
    const start = Math.atan2(hold.y - cy, hold.x - cx);
    const angle = start - beats * TAU * DRAGON.turn;
    const r = radius * lerp(DRAGON.from, DRAGON.to, smoothstep(clamp01((beats - 0.2) / 0.8)));
    const k = smoothstep(clamp01(beats / 0.4));
    head.x = lerp(hold.x, cx + Math.cos(angle) * r, k);
    head.y = lerp(hold.y, cy + Math.sin(angle) * r * DRAGON.squash, k);
    return head;
  }

  function drawDragon(view: GiantView, beats: number, cx: number, cy: number, radius: number) {
    const shown =
      smoothstep(clamp01(beats / 0.1)) *
      (1 - smoothstep(clamp01((beats - DRAGON.leave) / DRAGON.gone)));
    const grow = smoothstep(clamp01(beats / DRAGON.grow));
    pose.strikeX = cx;
    pose.strikeY = cy;
    pose.alpha = shown;
    if (shown <= 0) {
      return;
    }
    body.begin();
    for (let step = 0; step <= DRAGON.points; step += 1) {
      const share = step / DRAGON.points;
      const at = dragonHead(Math.max(0, beats - share * DRAGON.tail), cx, cy, radius);
      bodyX[step] = at.x;
      bodyY[step] = at.y;
      body.push(at.x, at.y, grow * DRAGON.width * (1 - 0.6 * share));
    }
    mane.begin();
    for (let index = 0; index < DRAGON.mane.to - DRAGON.mane.from; index += 1) {
      const step = DRAGON.mane.from + index;
      const dx = (bodyX[step + 1] ?? 0) - (bodyX[step - 1] ?? 0);
      const dy = (bodyY[step + 1] ?? 0) - (bodyY[step - 1] ?? 0);
      const length = Math.hypot(dx, dy) || 1;
      const width = grow * DRAGON.width * (1 - (0.6 * step) / DRAGON.points);
      const away = width * DRAGON.mane.away + DRAGON.mane.wave * Math.sin(step * 0.5 - beats * 3);
      mane.push(
        (bodyX[step] ?? 0) - (dy / length) * away,
        (bodyY[step] ?? 0) + (dx / length) * away,
        width * DRAGON.mane.width,
      );
    }
    const headX = bodyX[0] ?? 0;
    const headY = bodyY[0] ?? 0;
    if (Math.hypot(headX - (bodyX[DRAGON.points] ?? 0), headY - (bodyY[DRAGON.points] ?? 0)) < 4) {
      pose.alpha = 0;
      return;
    }
    body.curl(1, 8 * grow + 1, 1.2, 4 * grow);
    mane.curl(-1, 5, 1.1, 2);
    body.draw(ribbons, 1, AIR_SILK, shown);
    mane.draw(ribbons, -1, AIR_SILK, shown);
    pose.x = headX;
    pose.y = headY;
    pose.scale = 0.45 + 0.8 * grow;
    pose.turn = Math.atan2(headY - (bodyY[1] ?? headY), headX - (bodyX[1] ?? headX)) + Math.PI / 2;
    pose.limb = 0;
    view.limbs[0].visible = view.limbs[1].visible = false;
  }

  function onGiant(sprite: Sprite, px: number, py: number): void {
    const scale = pose.scale * GIANT.size;
    const cos = Math.cos(pose.turn);
    const sin = Math.sin(pose.turn);
    sprite.position.set(
      pose.x + (px * cos - py * sin) * scale,
      pose.y + (px * sin + py * cos) * scale,
    );
  }

  function placeBody(view: GiantView, shapes: GiantShapes, element: number): void {
    const { x, y, turn, alpha } = pose;
    const scale = pose.scale * GIANT.size;
    view.body.visible = alpha > 0;
    view.body.texture = (bodyShapes[element] ?? shapes.phoenix).texture;
    view.body.position.set(x, y);
    view.body.rotation = turn;
    view.body.scale.set(scale);
    view.body.alpha = alpha;
    const [right, left] = view.limbs;
    if (element === FIRE) {
      for (let side = -1; side <= 1; side += 2) {
        const wing = side > 0 ? right : left;
        wing.visible = alpha > 0;
        wing.texture = shapes.phoenixWing.texture;
        wing.anchor.set(0, 0.5);
        onGiant(wing, side * WING_ROOT.x, WING_ROOT.y);
        wing.rotation = turn + side * PHOENIX.flap * pose.limb;
        wing.scale.set(side * scale, scale);
        wing.alpha = alpha;
      }
    } else if (element === WATER) {
      right.visible = alpha > 0;
      left.visible = false;
      right.texture = shapes.koiTail.texture;
      right.anchor.set(0.5, 0);
      onGiant(right, 0, KOI_TAIL_ROOT);
      right.rotation = turn + pose.limb;
      right.scale.set(scale);
      right.alpha = alpha;
    } else {
      right.visible = left.visible = false;
    }
  }

  function placeFace(view: GiantView, element: number, beats: number, frame: Frame): void {
    const { turn, alpha } = pose;
    const scale = pose.scale * GIANT.size;
    const face = FACES[element] ?? FACES[0];
    const open = smoothstep(clamp01((beats - GIANT.eyes.from) / GIANT.eyes.over));
    const third = smoothstep(clamp01((beats - GIANT.third.from) / GIANT.third.over));
    view.eyes.visible = alpha > 0 && open > 0.04;
    view.eyes.texture = eyeShapes[element]?.texture ?? view.eyes.texture;
    onGiant(view.eyes, 0, face.y);
    view.eyes.rotation = turn;
    view.eyes.scale.set(scale, scale * open);
    view.eyes.alpha = alpha;
    view.third.visible = alpha > 0 && third > 0.04;
    view.third.texture = thirdShapes[element]?.texture ?? view.third.texture;
    onGiant(view.third, 0, face.y - THIRD_EYE * face.size);
    view.third.rotation = turn;
    view.third.scale.set(scale * third, scale);
    view.third.alpha = alpha;
    view.thirdGlow.visible = view.third.visible;
    view.thirdGlow.position.copyFrom(view.third.position);
    view.thirdGlow.scale.set((face.size * 0.9 * scale) / textures.halo.radius);
    view.thirdGlow.alpha = 0.7 * third * alpha * (frame.calm ? 0.5 : 1) * frame.light.haloAlpha;
    setTint(view.thirdGlow, 0xffffff);
  }

  function placeRays(view: GiantView, element: number, beats: number, frame: Frame): void {
    const { calm, light } = frame;
    const strength = calm ? RAYS.calmAlpha : RAYS.alpha;
    let x = pose.x;
    let y = pose.y;
    let shine =
      RAYS.shine *
      smoothstep(clamp01((beats - 0.15) / 0.15)) *
      (1 - smoothstep(clamp01((beats - 0.35) / 0.15)));
    let turn = 0.1 + beats * 0.08;
    const after = (beats - GIANT.strike) / (calm ? RAYS.calmSlower : 1);
    if (after >= 0) {
      x = pose.strikeX;
      y = pose.strikeY;
      shine = Math.exp(-after * 1.3) * (1 - smoothstep(clamp01((after - 1.3) / 0.5)));
      turn = 0.26 + after * 0.06;
    }
    const tint = RAY_TINTS[element] ?? RAY_TINTS[0];
    for (let index = 0; index < view.rays.length; index += 1) {
      const ray = view.rays[index];
      if (ray === undefined) {
        continue;
      }
      const long = index % 2 === 0;
      ray.visible = shine > 0.01;
      ray.position.set(x, y);
      ray.rotation = (index * TAU) / RAYS.count + turn;
      ray.scale.set((long ? RAYS.long : RAYS.short) / RAY, long ? 1 : RAYS.narrow);
      ray.alpha = shine * strength * light.haloAlpha;
      setTint(ray, tint);
    }
  }

  function drawStrike(view: GiantView, element: number, beats: number, frame: Frame): void {
    const after = beats - GIANT.strike;
    const tint = RAY_TINTS[element] ?? RAY_TINTS[0];
    view.flash.visible = !frame.calm && after >= 0 && after < 1;
    if (view.flash.visible) {
      view.flash.position.set(pose.strikeX, pose.strikeY);
      view.flash.scale.set(FLASH.radius / textures.halo.radius);
      view.flash.alpha = FLASH.alpha * Math.exp(-after * FLASH.decay) * 4;
      setTint(view.flash, 0xffffff);
    }
    if (after < 0 || after >= SHOCK.beats) {
      return;
    }
    const wide = smoothstep(after / SHOCK.beats);
    const { strikeX, strikeY } = pose;
    light
      .circle(strikeX, strikeY, (34 + 180 * wide) * SHOCK.scale)
      .stroke({ width: 2.5 * (1 - wide) + 0.5, color: 0xffffff, alpha: 0.7 * (1 - wide) })
      .circle(strikeX, strikeY, (26 + 140 * wide) * SHOCK.scale)
      .stroke({ width: 5 * (1 - wide) + 0.5, color: tint, alpha: 0.7 * (1 - wide) });
  }

  function drawCast(view: GiantView, element: number, beats: number, frame: Frame): void {
    const flare = clamp01(beats / CAST.beats);
    view.castGlow.visible = !frame.calm && flare < 1;
    if (flare >= 1) {
      return;
    }
    light.circle(hold.x, hold.y, lerp(CAST.from, CAST.to, smoothstep(flare))).stroke({
      width: 2.5,
      color: ELEMENT_COLORS[element] ?? ELEMENT_COLORS[0],
      alpha: 1 - flare,
    });
    view.castGlow.position.set(hold.x, hold.y - 6);
    view.castGlow.scale.set(CAST.glow / textures.halo.radius);
    view.castGlow.alpha = 0.5 * (1 - flare) * frame.light.haloAlpha;
    setTint(view.castGlow, 0xffffff);
  }

  function drawStreams(element: number, beats: number, frame: Frame): void {
    const shown =
      Math.sin(Math.PI * clamp01(beats / STREAMS.beats)) * STREAMS.alpha * (frame.calm ? 0.6 : 1);
    if (shown <= 0.01) {
      return;
    }
    const color = ELEMENT_COLORS[element] ?? ELEMENT_COLORS[0];
    for (let side = -1; side <= 1; side += 2) {
      flagTip(side, hold, tip);
      for (let pass = 0; pass < 2; pass += 1) {
        light
          .moveTo(tip.x, tip.y)
          .quadraticCurveTo(tip.x, pose.y + 10, pose.x, pose.y)
          .stroke(
            pass === 0
              ? { width: STREAMS.width, color, alpha: shown, cap: 'round' }
              : { width: STREAMS.core, color: 0xffffff, alpha: shown, cap: 'round' },
          );
      }
    }
  }

  function drawPool(loose: PropState, beats: number, frame: Frame): void {
    const shown =
      smoothstep(clamp01(beats / 0.1)) *
      (1 - smoothstep(clamp01((beats - GIANT.strike - 0.3) / 0.4)));
    if (shown <= 0) {
      return;
    }
    const { pool } = KOI;
    ground
      .ellipse(loose.x, loose.y, pool.rx, pool.ry)
      .fill({ color: 0x0a2b5a, alpha: shown })
      .stroke({ width: 1.5, color: ELEMENT_COLORS[1], alpha: shown });
    for (let ripple = 0; ripple < pool.ripples; ripple += 1) {
      const turn = frame.calm ? ripple / pool.ripples : (beats * 1.6 + ripple / pool.ripples) % 1;
      const r = 10 + turn * 30;
      ground
        .ellipse(loose.x, loose.y, r, r * (pool.ry / pool.rx))
        .stroke({ width: 1.2, color: 0xd2efff, alpha: 0.5 * (1 - turn) * shown });
    }
  }

  function drawDebris(beats: number): void {
    const share = (beats - GIANT.strike) / DEBRIS.beats;
    if (share < 0 || share >= 1) {
      return;
    }
    for (let index = 0; index < DEBRIS.count; index += 1) {
      const angle = Math.PI * 0.15 + (index * Math.PI * 0.7) / (DEBRIS.count - 1);
      const away = lerp(DEBRIS.from, DEBRIS.to, smoothstep(share));
      const x = pose.strikeX + Math.cos(angle) * away;
      const y = pose.strikeY + Math.sin(angle) * away - DEBRIS.lift * Math.sin(Math.PI * share);
      const cos = Math.cos(angle * 3) * DEBRIS.size;
      const sin = Math.sin(angle * 3) * DEBRIS.size;
      ground
        .moveTo(x - cos + sin, y - sin - cos)
        .lineTo(x + cos + sin, y + sin - cos)
        .lineTo(x + cos - sin, y + sin + cos)
        .lineTo(x - cos * 0.3 - sin, y - sin * 0.3 + cos)
        .closePath()
        .fill({ color: DEBRIS.color, alpha: 1 - share })
        .stroke({ width: 0.9, color: OUT, alpha: 0.9 * (1 - share) });
    }
  }

  function drawGiant(state: SimState, loose: PropState, alpha: number, frame: Frame): void {
    const owner = byId(state.players, loose.playerId);
    const effect = owner === undefined ? undefined : unleashes.get(owner.classId);
    if (
      owner === undefined ||
      effect === undefined ||
      isChained(loose, effect, state.tick, frame.tempo)
    ) {
      return;
    }
    const { shapes } = sheet.get();
    const view = viewPool(shapes).acquire(loose.id);
    const { tempo, calm } = frame;
    const element = loose.rank ?? FIRE;
    const total = unleashedTicks(effect, element, tempo);
    const beats = (total - loose.ticksLeft + alpha) / tempo.ticksPerBeat;
    const size = statValue(owner, 'skillRadiusMul', 1);
    holdOf(owner, alpha, frame, hold);
    darkness = Math.max(
      darkness,
      smoothstep(clamp01(beats / DARK.rise)) *
        (1 - smoothstep(clamp01((beats - GIANT.strike - DARK.hold) / DARK.fall))),
    );
    if (beats >= GIANT.strike) {
      frame.shakeTick = Math.max(
        frame.shakeTick,
        frame.now - (beats - GIANT.strike) * tempo.ticksPerBeat,
      );
    }
    drawCast(view, element, beats, frame);
    if (element === AIR) {
      const cx = lerp(loose.prevX, loose.x, alpha);
      const cy = lerp(loose.prevY, loose.y, alpha);
      drawDragon(view, beats, cx, cy, unleashedReach(effect, AIR, 0, size, tempo));
    } else {
      if (element === FIRE) {
        posePhoenix(loose, beats, calm);
      } else if (element === WATER) {
        drawPool(loose, beats, frame);
        poseKoi(loose, effect, size, beats, frame);
      } else {
        poseFist(loose, beats);
        drawDebris(beats);
      }
      pose.alpha *= smoothstep(clamp01(beats / GIANT.appear));
    }
    drawStreams(element, beats, frame);
    placeBody(view, shapes, element);
    placeFace(view, element, beats, frame);
    placeRays(view, element, beats, frame);
    drawStrike(view, element, beats, frame);
    view.glow.visible = pose.alpha > 0;
    view.glow.position.set(pose.x, pose.y);
    view.glow.scale.set((110 * pose.scale * GIANT.size) / textures.halo.radius);
    view.glow.alpha = 0.45 * pose.alpha * frame.light.haloAlpha;
    setTint(view.glow, ELEMENT_COLORS[element] ?? ELEMENT_COLORS[0]);
  }

  return {
    reset(): void {
      views?.releaseAll();
      dark.visible = false;
      ground.clear();
      light.clear();
      ribbons.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (unleashes.size === 0) {
        return;
      }
      ground.clear();
      light.clear();
      ribbons.clear();
      darkness = 0;
      views?.begin();
      for (const loose of state.props ?? []) {
        if (loose.kind === UNLEASHED) {
          drawGiant(state, loose, alpha, frame);
        }
      }
      views?.end();
      dark.visible = darkness > 0;
      if (dark.visible) {
        const margin = 4000;
        setTint(dark, DARK.color);
        dark.alpha = darkness * (frame.calm ? DARK.calmAlpha : DARK.alpha);
        dark.position.set(-margin, -margin);
        dark.width = state.arena.width + 2 * margin;
        dark.height = state.arena.height + 2 * margin;
      }
    },
    destroy(): void {
      ground.destroy();
      light.destroy();
      ribbons.destroy();
      dark.destroy();
      sheet.painted?.source.destroy();
    },
  };
}
